import appManager from '@app/appManager';
import { WebphoneCallEvent } from '@modules/SoftPhoneModule/types';
import TasksModule from '@modules/TasksModule';
import ToastModule from '@modules/ToastModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { TaskOutcome } from '@api/generated/graphql';
import { completeTask } from '@modules/SpeedToLeadTasksModule/mutations/completeTask';
import {
  startAcceptanceExpiry,
  cancelAcceptanceExpiry,
} from '@modules/SpeedToLeadTasksModule/acceptanceExpiry';
import {
  SpeedToLeadTaskAttributes,
  SPEED_TO_LEAD_OUTBOUND_TASK_TYPE,
} from '../types';

const logger = appManager.logger('SpeedToLead');

const AUTO_LOG_MAX_ATTEMPTS = 3;
const AUTO_LOG_RETRY_BASE_DELAY_MS = 200;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Auto-log a NO_ANSWER disposition and delete the task.
 *
 * Used on the second telephony failure when the AI outcome flag is on. The AI
 * outcome process only runs on phone.call.ended (which is never emitted for
 * telephony failures), so without intervention the task would be stuck — the
 * outcome section is hidden under the flag and the dial button stays disabled.
 *
 * Retries up to AUTO_LOG_MAX_ATTEMPTS with linear backoff. The task is deleted
 * whether the mutation succeeds or every retry fails, so the agent is never
 * blocked. A toast informs the CSR (info on success, critical on exhaustion).
 *
 * Re-entrancy guard via `isAutoLogging`: a duplicate phone.call.failed-general
 * arriving during the awaited retries would otherwise re-enter this branch and
 * start a second concurrent auto-log loop. The attribute is set before the
 * first await and intentionally never cleared — every path ends in deleteTask,
 * so the attribute disappears with the task. This relies on deleteTask being
 * terminal locally; if a future change resurrects the task (e.g. Apollo
 * optimistic-update rollback or stale subscription re-inserting it), the
 * resurrected task would carry isAutoLogging = true and be permanently locked
 * out of the auto-log path.
 */
async function autoLogAndDeleteTask(
  taskId: string,
  attributes: SpeedToLeadTaskAttributes,
): Promise<void> {
  if (attributes.isAutoLogging) {
    logger.info('stl:call-failed:second-attempt:auto-log-in-flight', {
      taskId,
      tenantId: attributes.tenantId,
    });
    return;
  }
  TasksModule.setTaskAttribute(taskId, 'isAutoLogging', true);

  let attempts = 0;
  let lastErr: unknown;
  let logged = false;
  while (attempts < AUTO_LOG_MAX_ATTEMPTS && !logged) {
    attempts++;
    try {
      await completeTask({
        taskId,
        tenantId: attributes.tenantId,
        input: {
          outcome: TaskOutcome.NoAnswer,
          notes: 'Auto-logged: call failed twice (telephony failure).',
        },
      });
      logged = true;
    } catch (err) {
      lastErr = err;
      if (attempts < AUTO_LOG_MAX_ATTEMPTS) {
        await sleep(AUTO_LOG_RETRY_BASE_DELAY_MS * attempts);
      }
    }
  }

  TasksModule.deleteTask(taskId);

  if (logged) {
    logger.info('stl:call-failed:second-attempt:auto-logged', {
      taskId,
      tenantId: attributes.tenantId,
      attempts,
    });
    ToastModule.showToast({
      title:
        'Call failed twice. The task was auto-logged as No Answer and closed.',
      status: 'info',
    });
  } else {
    logger.error('stl:call-failed:second-attempt:auto-log-exhausted', {
      taskId,
      tenantId: attributes.tenantId,
      attempts,
      err:
        lastErr instanceof Error
          ? {
              message: lastErr.message,
              stack: lastErr.stack,
              name: lastErr.name,
            }
          : { value: String(lastErr) },
    });
    ToastModule.showToast({
      title:
        'Call failed twice and could not be auto-logged. The task has been closed.',
      status: 'critical',
    });
  }
}

/**
 * Handles phone.call.failed-general for STL tasks.
 *
 * A telephony-level failure means the call never connected (infrastructure or
 * carrier issue). SoftPhoneModule emits phone.call.failed-general for every
 * webphone:call-failed event regardless of reason. No-answer and bad-number
 * outcomes fire phone.call.ended instead and are handled by the normal
 * post-call completion path.
 *
 * Redial is allowed exactly once:
 * - First failure: reset hasMadeCall so the Dial CTA is re-enabled, restart
 *   the acceptance expiry from zero, and mark hasUsedRedial = true.
 * - Second failure: the redial opportunity is exhausted. Behavior depends on
 *   the featureSpeedToLeadOutboundingAiTaskOutcome flag:
 *
 *   - Flag OFF (default): set hasCallEnded = true so the task enters the
 *     post-call state (button disabled, outcome section visible) and the CSR
 *     can manually log a disposition.
 *
 *   - Flag ON: delegate to autoLogAndDeleteTask, which auto-logs a NO_ANSWER
 *     disposition with retries and then deletes the task.
 *
 * LOOKUP STRATEGY: The webphone:call-failed payload provides an empty phoneNumber
 * field (the SIP failure fires before the remote identity is resolved). Instead we
 * find the active STL task by type + call state — only one STL task can be active
 * (hasMadeCall = true) or in the redial window (hasUsedRedial = true) at a time,
 * so no phone-number disambiguation is needed.
 *
 * KNOWN LIMITATION: this lookup does not verify that the failed call belongs to
 * the STL task — a concurrent inbound or unrelated outbound call failure would
 * incorrectly trigger this handler. Acceptable for v1 given the single-task
 * constraint, but will need callId tracking if concurrent call scenarios are added.
 */
async function handleStlCallFailed(_event: WebphoneCallEvent): Promise<void> {
  const stlTask = TasksModule.getTasks().find(
    (t) =>
      t.type === SPEED_TO_LEAD_OUTBOUND_TASK_TYPE &&
      ((t.attributes as SpeedToLeadTaskAttributes).hasMadeCall ||
        (t.attributes as SpeedToLeadTaskAttributes).hasUsedRedial),
  );
  if (!stlTask) return;

  const attributes = stlTask.attributes as SpeedToLeadTaskAttributes;

  if (attributes.hasUsedRedial) {
    // Redial already used — cancel any running expiry timer so a duplicate SIP
    // event during the redial window cannot delete the task mid-disposition.
    cancelAcceptanceExpiry();

    if (
      FeatureFlagModule.isEnabled(
        FeatureFlagKeys.featureSpeedToLeadOutboundingAiTaskOutcome,
      )
    ) {
      await autoLogAndDeleteTask(stlTask.id, attributes);
      return;
    }

    logger.info('stl:call-failed:second-attempt', {
      taskId: stlTask.id,
      tenantId: attributes.tenantId,
      action: 'post-call-state-entered',
    });
    TasksModule.setTaskAttribute(stlTask.id, 'hasCallEnded', true);
    return;
  }

  // First failure — offer one redial
  logger.info('stl:call-failed:first-attempt', {
    taskId: stlTask.id,
    tenantId: attributes.tenantId,
    action: 'dial-re-enabled-expiry-restarted',
  });
  TasksModule.setTaskAttribute(stlTask.id, 'hasMadeCall', false);
  TasksModule.setTaskAttribute(stlTask.id, 'hasUsedRedial', true);
  startAcceptanceExpiry(attributes.tenantId, stlTask.id);
}

export default handleStlCallFailed;
